import * as React from "react";
import { observer } from "mobx-react";

import { UitkEGMaps, UitkEGMapsType, UitkEGMapsQualifier, UitkEGMapsRef } from "@egds/react-core/map";
import { Bounds, FeatureObject, EGMapConfiguration, Coordinates, CoordinatesArray } from "@eg-maps/core";
import { useBexApi } from "@shared-ui/bex-api-context";
import { runInAction } from "mobx";
import { useLogger } from "@shared-ui/logger-context";
import { useLazyQuery } from "@apollo/client";

// Types
import { MapsFlexModuleResult } from "src/typings/microserviceModels/maps-flex-module";
import { MapPlace } from "typings/map/eg-map";
import { ModuleMapProps, ModuleMapQueryComponentProps } from "./typings";
import { withStores } from "stores";
import { EGMapDisplayCard } from "./components/EGMapDisplayCard";
import { EGMapSearchButton } from "./components/EGMapSearchButton";

import { useLocalization } from "@shared-ui/localization-context";
import { UitkToolbar, UitkToolbarProps } from "@egds/react-core/toolbar";
import { UitkSheet } from "@egds/react-core/sheet";
import { useMapContext } from "./MapContext";
import { convertToEgMapsMarkers, registerViewportChangeListener, mapTracking, getCenter } from "./EGMapsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { MapPropertySearchByLatLongDocument, MapPropertySearchByLatLongQueryVariables } from "__generated__/typedefs";
import { DISTANCE } from "../Hotels/utils/constants";

export const EGMapsModule = withStores(
  "context",
  "analytics",
  "hotels",
  "flexModuleModelStore",
  "flightsStore"
)(
  observer((props: ModuleMapProps) => {
    const { templateComponent, flexModuleModelStore } = props;

    if (!templateComponent) {
      return null;
    }

    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as MapsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    return <EGMapsQueryComponent {...props} model={model} />;
  })
);

const EGMapsQueryComponent = withStores(
  "context",
  "analytics",
  "hotels",
  "flexModuleModelStore"
)(
  observer((props: ModuleMapQueryComponentProps) => {
    const { toggleShowFullscreenMap, sendUserToSRP, shouldOpenMapInSRP } = useMapContext();
    const [selectedFeature, setSelectedFeature] = React.useState<MapPlace>();
    const [changedBounds, setChangedBounds] = React.useState<Bounds>();
    const mapRef = React.useRef<UitkEGMapsRef>(null);
    const { templateComponent, model, context, hotels: hotelsStore } = props;

    if (!templateComponent) {
      return null;
    }

    const componentName = templateComponent.type || "";
    const { formatText } = useLocalization();
    const { error: logError } = useLogger();
    const { context: bexApiContext } = useBexApi();
    const triggerRef = React.createRef<HTMLElement>();

    const track = useClickTracker();
    const location = model.location ?? context.searchContext.location;
    const geoCoordinate = location?.geoCoordinate;

    const [fetchProperties, { data, loading, error }] = useLazyQuery(MapPropertySearchByLatLongDocument);

    React.useEffect(() => {
      if (error && !loading) {
        logError("MAP_PROPERTY_SEARCH_BY_LAT_LONG_QUERY_ERROR", `message=${error}`);
        runInAction(() => {
          hotelsStore.hasMapErrorMessage = true;
        });
      }
    }, [error]);

    const onMarkerSelectionChanged = React.useCallback(
      (event) => {
        const { action, feature } = event;
        switch (action) {
          case "clearSelection": {
            setSelectedFeature(undefined);
            break;
          }
          case "select": {
            const selectedFeature: MapPlace | undefined =
              hotelsStore.allHotelsOnMapLatLong.length === 0
                ? hotelsStore.allHotelsOnMap.find((hotel) => hotel.id === feature.id)
                : hotelsStore.allHotelsOnMapLatLong.find((hotel) => hotel.id === feature.id);

            setSelectedFeature(selectedFeature);

            const rfrrString =
              selectedFeature?.icon?.type === "place"
                ? "fullscreen.marker.poi"
                : `fullscreen.marker.${selectedFeature?.id}`;

            mapTracking(componentName, track, rfrrString);
            break;
          }
          default:
            break;
        }
      },
      [setSelectedFeature]
    );

    if (data && !loading) {
      hotelsStore.setAllHotelsOnMapOnHotelsByLatLongSuccessResponse(data.propertySearch, location.id.toString(10));
      runInAction(() => {
        hotelsStore.hasMapErrorMessage = false;
      });
    }

    const locationMarker = location && {
      id: location.id,
      latitude: geoCoordinate?.latitude,
      longitude: geoCoordinate?.longitude,
      icon: { type: location.type },
    };

    const markersToEnrich =
      hotelsStore.allHotelsOnMapLatLong.length === 0
        ? [...hotelsStore.allHotelsOnMap]
        : [...hotelsStore.allHotelsOnMapLatLong];

    locationMarker && markersToEnrich.push({ ...locationMarker } as MapPlace);

    let markers: FeatureObject[] = React.useMemo(() => convertToEgMapsMarkers(markersToEnrich, formatText), [
      hotelsStore.allHotelsOnMap,
      hotelsStore.allHotelsOnMapLatLong,
    ]);

    if (hotelsStore.allHotelsOnMap.length === 0 && hotelsStore.allHotelsOnMapLatLong.length === 0 && locationMarker) {
      markers = [
        {
          id: location.id,
          lat: geoCoordinate?.latitude,
          lng: geoCoordinate?.longitude,
          type: "property",
          status: "unknown",
        },
      ];
    }

    if (!markers) {
      return null;
    }

    const initialViewport = React.useMemo(
      () => markers?.map((f) => [f.lat, f.lng] as Coordinates) as CoordinatesArray,
      []
    );

    const updateMarkers = React.useCallback((map) => {
      const bounds = map.pushFeatureData(markers, "replace");
      if (bounds) map.fitBounds(bounds.sw, bounds.ne);
    }, []);

    const onInit = React.useCallback(
      (map) => {
        updateMarkers(map);
      },
      [updateMarkers]
    );

    const beforeInit = React.useCallback(
      (map) => {
        registerViewportChangeListener(map, onMapZoomIn, onMapZoomOut, onMapPan);
        map.addBoundChangeListener(({ bounds }: any) => {
          setChangedBounds(bounds);
        });
        map.addSelectionChangeListener(onMarkerSelectionChanged);
      },
      [onMarkerSelectionChanged, setChangedBounds]
    );

    const onMapPan = () => {
      mapTracking(componentName, track, "fullscreen.map.pan");
    };

    const onMapZoomIn = () => {
      mapTracking(componentName, track, "fullscreen.map.zoom.in");
    };

    const onMapZoomOut = () => {
      mapTracking(componentName, track, "fullscreen.map.zoom.out");
    };

    const onSearch = () => {
      const mapCenter = getCenter(mapRef.current?.map?.());

      mapTracking(componentName, track, "fullscreen.map.search");

      if (!context || !mapCenter?.lat || !mapCenter?.lng) {
        return;
      }

      const destination = {
        coordinates: {
          latitude: mapCenter.lat,
          longitude: mapCenter.lng,
        },
      };

      const queryVariables: MapPropertySearchByLatLongQueryVariables = {
        context: bexApiContext,
        destination: destination,
        criteria: {
          primary: {
            destination,
            dateRange: hotelsStore.getSearchDateRange(),
            rooms: [],
          },
          secondary: {
            selections: [{ id: "sort", value: DISTANCE }],
          },
        },
      };

      fetchProperties({ variables: queryVariables });
    };

    const toolBarAttributes: UitkToolbarProps = {
      toolbarTitle: location?.extendedName,
      navigationContent: {
        navIconLabel: formatText("maps.fullscreen.close.a11yLabel"),
        onClick: toggleShowFullscreenMap,
      },
    };

    const closeCallback = shouldOpenMapInSRP ? sendUserToSRP : toggleShowFullscreenMap;

    const formatFunction = (formatText: (key: string, ...args: any[]) => string) => (key: string, args: string[]) =>
      key === "propertyClusterLabelWithPrice"
        ? formatText("egmaps.markers.cluster.available.label", Number(args[0]), `${args[1]}`)
        : undefined;

    const egMapsConfig: EGMapConfiguration = {
      googleMaps: {
        gmapsAddConfig: {
          mapId: "466ac5e186f00481",
        },
      },
      markers: {
        place: {
          zIndex: {
            ["unknown"]: 0,
          },
        },
        property: {
          zIndex: {
            [`${UitkEGMapsQualifier.NOT_CLUSTERED}`]: 0,
          },
        },
      },
      i18n: {
        formatFunction: formatFunction(formatText),
      },
      locale: context.locale,
    };

    return (
      <UitkSheet type="full" isVisible closeCallback={closeCallback} triggerRef={triggerRef}>
        <UitkToolbar {...toolBarAttributes} />
        <UitkEGMaps
          accessToken="AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68"
          beforeInit={beforeInit}
          features={markers}
          initialViewport={initialViewport}
          innerRef={mapRef}
          mapConfig={egMapsConfig}
          mapType={UitkEGMapsType.GOOGLE}
          onInit={onInit}
        >
          {selectedFeature && <EGMapDisplayCard componentName={componentName} place={selectedFeature} isVisible />}
          <EGMapSearchButton
            changedBounds={changedBounds}
            isSearchInProgress={loading}
            onMapSearch={onSearch}
            searchMessage={formatText("maps.fullscreen.searchThisArea")}
          />
        </UitkEGMaps>
      </UitkSheet>
    );
  })
);

EGMapsModule.displayName = "EGMapsModule";

export default EGMapsModule;
